/**
 * Rankings Card Picker Modal (Create Video)
 * Opens a rankings-style card grid and writes selection back to the hidden <select>.
 */

import { observeManyWhenVisible } from '../../../core/utils/dom.js';
import { pickStandingActionId } from '../../utils/spriteFallbacks.js';
import { normalizeGender } from '../../utils/genderUtils.js';
import { characterIsNsfw } from '../../utils/nsfwTags.js';
import { getNsfwEnabled } from '../../utils/userPrefs.js';
import { filterBlockedCharacters, ensureBlockedContentLoaded } from '../../moderation/blockedContentStore.js';
import { fetchCache } from '../../utils/fetchCache.js';
import {
  bindPickerCardMedia,
  createPickerCardMediaEl,
  fetchPickerCardMedia,
  isPrivateCharacter,
  releasePickerCardMedia,
  resolvePickerCardMedia,
} from '../../rankings/services/characterCardMedia.js';

const toStr = (v) => (v === null || v === undefined ? '' : String(v));
const normalize = (v) => toStr(v).toLowerCase().trim();

const resolveMessagesTotalTooltip = () => {
  const lang = (typeof window.getCurrentLanguage === 'function' && window.getCurrentLanguage()) || 'en';
  const rankingsT = window.translations?.[lang]?.rankings || window.translations?.en?.rankings || {};
  return rankingsT['rankings.messagesTotalTooltip'] || 'Total number of messages';
};

const ensureRankingsTooltipTranslations = async () => {
  try {
    const { getCurrentLanguage, loadTranslations } = await import('../../translations.js');
    const lang = getCurrentLanguage();
    if (!window.translations?.[lang]?.rankings?.['rankings.messagesTotalTooltip']) {
      await loadTranslations('rankings', lang);
    }
  } catch {
    // Fallback English tooltip remains available.
  }
};

const ensureModal = () => {
  let modal = document.getElementById('create-video-character-picker-modal');
  if (modal) return modal;

  modal = document.createElement('div');
  modal.id = 'create-video-character-picker-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  const panel = document.createElement('div');
  panel.className = 'picker-panel';

  modal.appendChild(panel);
  document.body.appendChild(modal);
  return modal;
};

const openModal = (modal) => {
  if (!modal) return;
  modal.classList.add('show');
};

const closeModal = (modal) => {
  if (!modal) return;
  modal.classList.remove('show');
};

const formatPopularity = (pop) => {
  const num = Number.parseInt(toStr(pop), 10);
  if (!Number.isFinite(num)) return '0';
  if (num >= 1_000_000) return `${Math.floor(num / 100_000) / 10}M`;
  if (num >= 1_000) return `${Math.floor(num / 100) / 10}k`;
  return String(num);
};

const INFO_SKIP_KEYS = new Set(['race', 'lora_activation']);

const formatInfoLabel = (key) =>
  String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const valueToDisplayString = (val) => {
  if (Array.isArray(val)) {
    return val
      .map((item) => (typeof item === 'string' ? item : item?.name ?? item))
      .filter((v) => v != null && String(v).trim() !== '')
      .map((v) => String(v).trim())
      .join(', ');
  }
  return String(val);
};

const collectInfoEntries = (info) => {
  if (!info || typeof info !== 'object' || Array.isArray(info)) return [];
  return Object.entries(info).flatMap(([key, val]) => {
    if (INFO_SKIP_KEYS.has(key)) return [];
    if (val == null || val === '') return [];
    if (typeof val === 'object' && !Array.isArray(val)) return [];
    const text = valueToDisplayString(val).trim();
    if (!text) return [];
    return [{ key, label: formatInfoLabel(key), text }];
  });
};

const resolvePickerLanguage = () =>
  (typeof window.getCurrentLanguage === 'function' && window.getCurrentLanguage()) || 'en';

const resolveCharacterInfoCopy = () => {
  const lang = resolvePickerLanguage();
  return window.translations?.[lang]?.rankings || window.translations?.en?.rankings || {};
};

const removePickerInfoTooltip = () => {
  document.querySelectorAll('.picker-character-info-tooltip').forEach((el) => el.remove());
};

const positionPickerInfoTooltip = (tooltip, iconElement) => {
  if (!iconElement) return;
  const iconRect = iconElement.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const tooltipWidth = tooltipRect.width;
  const spaceOnLeft = iconRect.left;
  const spaceOnRight = window.innerWidth - iconRect.right;

  if (spaceOnLeft >= tooltipWidth + 10) {
    tooltip.style.right = `${window.innerWidth - iconRect.left + 10}px`;
    tooltip.style.top = `${iconRect.top}px`;
  } else if (spaceOnRight >= tooltipWidth + 10) {
    tooltip.style.left = `${iconRect.right + 10}px`;
    tooltip.style.top = `${iconRect.top}px`;
  } else {
    tooltip.style.left = '50%';
    tooltip.style.top = `${iconRect.bottom + 10}px`;
    tooltip.style.transform = 'translateX(-50%)';
  }
};

const attachPickerInfoTooltipDismiss = (tooltip) => {
  const closeTooltip = (event) => {
    if (event?.type === 'click' && event.target instanceof Element) {
      if (tooltip.contains(event.target) || event.target.closest('.description-expand-icon')) return;
    }
    tooltip.remove();
    document.removeEventListener('click', closeTooltip);
    document.removeEventListener('keydown', handleEscape);
  };

  const handleEscape = (e) => {
    if (e.key === 'Escape') closeTooltip(e);
  };

  setTimeout(() => {
    document.addEventListener('click', closeTooltip);
    document.addEventListener('keydown', handleEscape);
  }, 100);
};

const showPickerCharacterInfoTooltip = async (characterId, characterName, iconElement, isPrivate) => {
  const t = resolveCharacterInfoCopy();
  removePickerInfoTooltip();

  const tooltip = document.createElement('div');
  tooltip.className = 'description-tooltip picker-character-info-tooltip';
  const header = document.createElement('div');
  header.className = 'description-tooltip-header';
  const titleElement = document.createElement('h4');
  titleElement.className = 'description-tooltip-title';
  titleElement.textContent = characterName || t['rankings.moderation.characterInfo'] || 'Character';
  header.appendChild(titleElement);
  tooltip.appendChild(header);
  tooltip.addEventListener('click', (event) => event.stopPropagation());

  try {
    const details = await fetchCache.fetchCharacterDetails(characterId, isPrivate, resolvePickerLanguage());
    const info = details?.info && typeof details.info === 'object' && !Array.isArray(details.info)
      ? details.info
      : {};
    const entries = collectInfoEntries(info);
    if (entries.length) {
      const list = document.createElement('dl');
      list.className = 'description-tooltip-info';
      entries.forEach((entry) => {
        const dt = document.createElement('dt');
        dt.textContent = entry.label;
        const dd = document.createElement('dd');
        dd.textContent = entry.text;
        list.appendChild(dt);
        list.appendChild(dd);
      });
      tooltip.appendChild(list);
    } else {
      const empty = document.createElement('p');
      empty.className = 'description-tooltip-text';
      empty.textContent = t['rankings.moderation.characterInfoUnavailable'] || 'Character details are unavailable.';
      tooltip.appendChild(empty);
    }
  } catch (error) {
    console.error('showPickerCharacterInfoTooltip', error);
    const fallback = document.createElement('p');
    fallback.className = 'description-tooltip-text';
    fallback.textContent = t['rankings.moderation.characterInfoUnavailable'] || 'Character details are unavailable.';
    tooltip.appendChild(fallback);
  }

  document.body.appendChild(tooltip);
  positionPickerInfoTooltip(tooltip, iconElement);
  requestAnimationFrame(() => tooltip.classList.add('is-visible'));
  attachPickerInfoTooltipDismiss(tooltip);
};

const appendPickerCharacterInfoExpandIcon = (imageWrap, item) => {
  if (!imageWrap || item?.id == null) return;
  const t = resolveCharacterInfoCopy();
  const expandIcon = document.createElement('button');
  expandIcon.type = 'button';
  expandIcon.className = 'description-expand-icon';
  expandIcon.innerHTML = '&#8505;';
  expandIcon.tabIndex = 0;
  expandIcon.setAttribute(
    'aria-label',
    t['rankings.moderation.characterInfo'] || `About ${item.name || 'character'}`
  );
  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    void showPickerCharacterInfoTooltip(item.id, item.name, expandIcon, !!item.private);
  };
  expandIcon.addEventListener('click', handleOpen);
  expandIcon.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    handleOpen(e);
  });
  imageWrap.appendChild(expandIcon);
};

const buildRankedList = (characters, q) => {
  const query = normalize(q);
  const nsfwAllowed = getNsfwEnabled();
  const list = (Array.isArray(characters) ? characters : [])
    .filter((c) => nsfwAllowed || !characterIsNsfw(c))
    .map((c) => {
      const isPrivate = isPrivateCharacter(c);
      const rankNum = Number(c?.rank);
      return {
        ...c,
        id: toStr(c?.id).trim(),
        name: toStr(c?.name).trim() || 'Unknown',
        image: toStr(c?.image).trim() || '/assets/images/kiss.webp',
        rank: Number.isFinite(rankNum) ? rankNum : null,
        pop: c?.pop ?? c?.total_messages_sent ?? 0,
        gender: c?.gender ?? null,
        custom: !!(c?.custom || c?.isCustom),
        customBadge: toStr(c?.customBadge).trim(),
        private: isPrivate,
      };
    })
    .filter((c) => !!c.id)
    .filter((c) => !query || normalize(c.name).includes(query));

  const visible = filterBlockedCharacters(list);
  visible.sort((a, b) => {
    const aPinned = a.custom || a.private;
    const bPinned = b.custom || b.private;
    if (aPinned !== bPinned) return aPinned ? -1 : 1;
    if (a.private !== b.private) return a.private ? -1 : 1;
    const ar = a.rank ?? Number.POSITIVE_INFINITY;
    const br = b.rank ?? Number.POSITIVE_INFINITY;
    if (ar !== br) return ar - br;
    return a.name.localeCompare(b.name);
  });

  return visible;
};

let spriteCache = null;
let spritePending = null;

const getSpriteCaches = () => {
  if (!spriteCache) spriteCache = new Map();
  if (!spritePending) spritePending = new Map();
  return { spriteCache, spritePending };
};

const fetchSpriteUrlForCard = async (characterId, gender) => {
  const id = toStr(characterId).trim();
  if (!id) return '/assets/images/kiss.webp';

  const g = normalizeGender(gender);
  const key = `${id}:${g}`;
  const caches = getSpriteCaches();

  if (caches.spriteCache.has(key)) return caches.spriteCache.get(key);
  if (caches.spritePending.has(key)) return caches.spritePending.get(key);

  const promise = (async () => {
    try {
      const defaultActionId = pickStandingActionId(g, id);
      const data = await fetchCache.fetchCharacterSprite(id, defaultActionId, 0, g, false);
      const url = data?.spriteUrl || '/assets/images/kiss.webp';
      caches.spriteCache.set(key, url);
      return url;
    } catch (error) {
      console.warn('[CreateVideo Ranked Picker] sprite fetch failed', { id, gender: g }, error);
      const url = '/assets/images/kiss.webp';
      caches.spriteCache.set(key, url);
      return url;
    } finally {
      caches.spritePending.delete(key);
    }
  })();

  caches.spritePending.set(key, promise);
  return promise;
};

export const openCreateVideoRankedCharacterPicker = async ({
  selectId,
  getCharacters,
  getFilterIds,
  getTakenIds,
  onSelect,
  showCreateCard = true,
} = {}) => {
  const selectEl = selectId ? document.getElementById(selectId) : null;
  if (!selectEl && typeof onSelect !== 'function') return;

  const modal = ensureModal();
  const panel = modal.querySelector('.picker-panel');
  if (!panel) return;

  await ensureRankingsTooltipTranslations();
  await ensureBlockedContentLoaded();

  const currentValue = selectEl ? toStr(selectEl.value).trim() : '';
  const takenIdsRaw = (typeof getTakenIds === 'function' ? getTakenIds() : []) || [];
  const takenIds = new Set(
    (Array.isArray(takenIdsRaw) ? takenIdsRaw : [])
      .map((value) => toStr(value).trim())
      .filter(Boolean)
      .filter((id) => id !== currentValue)
  );
  let searchTerm = '';
  let filterMode = 'all'; // 'all' | 'slot'
  let filterModeInitialized = false;

  const cleanup = { keydown: null };

  const handleClose = () => {
    if (cleanup.keydown) document.removeEventListener('keydown', cleanup.keydown, true);
    cleanup.keydown = null;
    removePickerInfoTooltip();
    releasePickerCardMedia(panel);
    closeModal(modal);
  };

  const render = () => {
    removePickerInfoTooltip();
    releasePickerCardMedia(panel);
    panel.innerHTML = '';

    const header = document.createElement('div');
    header.className = 'picker-header';

    const title = document.createElement('div');
    title.className = 'picker-title';
    title.textContent = 'Select a character';

    // Optional: allow callers (scenario slots) to constrain the list to a specific id(s),
    // e.g. only show the character that currently has a preview in that slot.
    const filterIdsRaw = (typeof getFilterIds === 'function' ? getFilterIds() : []) || [];
    const filterIds = Array.isArray(filterIdsRaw)
      ? filterIdsRaw.map(v => toStr(v).trim()).filter(Boolean)
      : [];
    const canFilter = filterIds.length > 0;
    // Only auto-default once per modal open (don't override user's toggle on re-render).
    if (!filterModeInitialized) {
      filterMode = canFilter ? 'slot' : 'all';
      filterModeInitialized = true;
    }

    const leftControls = document.createElement('div');
    leftControls.style.display = 'flex';
    leftControls.style.alignItems = 'center';
    leftControls.style.gap = '10px';
    leftControls.appendChild(title);

    if (canFilter) {
      const toggleBtn = document.createElement('button');
      toggleBtn.type = 'button';
      toggleBtn.className = 'picker-close'; // reuse minimal button styling
      toggleBtn.style.fontSize = '12px';
      toggleBtn.style.padding = '6px 10px';
      toggleBtn.style.borderRadius = '999px';
      toggleBtn.style.border = '1px solid rgba(255,255,255,0.18)';
      toggleBtn.style.background = 'rgba(255,255,255,0.06)';
      toggleBtn.style.color = 'rgba(255,255,255,0.9)';
      toggleBtn.textContent = (filterMode === 'slot') ? 'Show all' : 'Only this slot';
      toggleBtn.setAttribute('aria-label', (filterMode === 'slot') ? 'Show all characters' : 'Show only this slot');
      toggleBtn.addEventListener('click', () => {
        filterMode = (filterMode === 'slot') ? 'all' : 'slot';
        render();
      });
      leftControls.appendChild(toggleBtn);
    }

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'picker-close';
    closeBtn.textContent = '×';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', handleClose);

    header.appendChild(leftControls);
    header.appendChild(closeBtn);

    const searchWrap = document.createElement('div');
    searchWrap.className = 'picker-search';

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.value = searchTerm;
    searchInput.placeholder = 'Search characters...';
    searchInput.setAttribute('aria-label', 'Search characters');
    searchInput.addEventListener('input', (e) => {
      searchTerm = e?.target?.value || '';
      render();
    });

    searchWrap.appendChild(searchInput);

    const listWrap = document.createElement('div');
    listWrap.className = 'picker-list';

    const grid = document.createElement('div');
    grid.className = 'picker-grid';

    const chars = getCharacters ? getCharacters() : [];
    let items = buildRankedList(chars, searchTerm);
    if (canFilter && filterMode === 'slot') {
      const allowed = new Set(filterIds);
      items = items.filter(item => allowed.has(item.id));
    }

    const pendingMedia = [];
    items.forEach((item) => {
      const isSelected = item.id === currentValue;
      const isTaken = takenIds.has(item.id);
      const isNumericId = /^\d+$/.test(item.id);
      const skipSpriteFetch = !isNumericId || item.custom;
      const mediaSpec = skipSpriteFetch ? null : resolvePickerCardMedia(item);

      const card = document.createElement('div');
      card.className = 'character-card';
      card.setAttribute('role', 'button');
      card.tabIndex = 0;
      card.setAttribute('aria-disabled', isTaken ? 'true' : 'false');
      card.setAttribute('aria-label', isTaken ? `${item.name} already selected in another slot` : `Select ${item.name}`);
      if (isTaken) card.classList.add('party-pick-disabled');

      const inner = document.createElement('div');
      inner.className = 'card-inner';

      const front = document.createElement('div');
      front.className = 'card-front';

      const media = createPickerCardMediaEl({
        alt: item.name,
        kind: mediaSpec?.kind === 'video' ? 'video' : 'image',
      });
      media.dataset.characterId = item.id;
      media.dataset.gender = toStr(item.gender);
      media.dataset.spriteLoaded = 'false';
      if (media instanceof HTMLImageElement) {
        media.src = '/assets/images/kiss.webp';
        media.onerror = () => { media.src = '/assets/images/kiss.webp'; };
      }
      if (skipSpriteFetch) {
        media.dataset.noSpriteFetch = 'true';
        media.dataset.spriteLoaded = 'true';
        if (item.image && item.image !== '/assets/images/kiss.webp' && media instanceof HTMLImageElement) {
          media.src = item.image;
        }
      }
      pendingMedia.push({ media, item });

      const badge = document.createElement('div');
      badge.className = 'picker-badge';
      badge.textContent = item.custom
        ? (item.customBadge || 'Custom')
        : (item.private ? 'Private' : (item.rank ? `#${item.rank}` : '—'));

      const selectedBadge = (isSelected || isTaken) ? document.createElement('div') : null;
      if (selectedBadge) {
        selectedBadge.className = 'picker-selected';
        selectedBadge.textContent = isTaken ? 'In use' : 'Selected';
      }

      const nameEl = document.createElement('h3');
      nameEl.textContent = item.name;

      const popEl = document.createElement('div');
      popEl.className = 'message-count';
      popEl.title = resolveMessagesTotalTooltip();
      popEl.setAttribute('data-translate-attr', 'title:rankings.messagesTotalTooltip');
      popEl.innerHTML = `<i class="fas fa-comment"></i> ${formatPopularity(item.pop)}`;

      front.appendChild(media);
      front.appendChild(nameEl);
      front.appendChild(popEl);

      inner.appendChild(front);
      inner.appendChild(badge);
      if (selectedBadge) inner.appendChild(selectedBadge);
      appendPickerCharacterInfoExpandIcon(inner, item);

      card.appendChild(inner);

      const handlePick = () => {
        if (isTaken) return;
        if (typeof onSelect === 'function') {
          onSelect(item);
          handleClose();
          return;
        }
        if (!selectEl) return;
        if (!Array.from(selectEl.options || []).some((opt) => opt.value === item.id)) {
          selectEl.add(new Option(item.name, item.id));
        }
        if (selectEl.value !== item.id) {
          selectEl.value = item.id;
          selectEl.dispatchEvent(new Event('change', { bubbles: true }));
        }
        window.searchDropdownRegistry?.[selectEl.id]?.refresh?.();
        handleClose();
      };

      card.addEventListener('click', handlePick);
      card.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (e.target instanceof Element && e.target.closest('.description-expand-icon')) return;
        e.preventDefault();
        handlePick();
      });

      grid.appendChild(card);
    });

    if (showCreateCard) {
      const createCard = document.createElement('div');
      createCard.className = 'character-card rest create-character-card';
      createCard.setAttribute('role', 'button');
      createCard.setAttribute('tabindex', '0');
      createCard.setAttribute('aria-label', 'Create a character');
      createCard.innerHTML = `
        <div class="card-inner">
          <div class="card-front create-character-front">
            <div class="create-character-plus" aria-hidden="true">+</div>
            <div class="create-character-title">Create Character</div>
          </div>
        </div>
      `;
      const handleCreateActivate = () => {
        window.router?.navigate('/create-character') || (window.location.href = 'create-character.html');
      };
      createCard.addEventListener('click', handleCreateActivate);
      createCard.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        handleCreateActivate();
      });
      grid.appendChild(createCard);
    }

    listWrap.appendChild(grid);

    panel.appendChild(header);
    panel.appendChild(searchWrap);
    panel.appendChild(listWrap);

    requestAnimationFrame(() => {
      searchInput.focus();
    });

    const itemByMedia = new WeakMap(pendingMedia.map(({ media, item }) => [media, { item }]));
    const mediaEls = pendingMedia.map(({ media }) => media);
    if (mediaEls.length) {
      const loadMedia = async (mediaEl) => {
        if (!mediaEl || mediaEl.dataset.spriteLoaded === 'true') return;
        if (mediaEl.dataset.noSpriteFetch === 'true') return;
        mediaEl.dataset.spriteLoaded = 'true';
        const bound = itemByMedia.get(mediaEl);
        const item = bound?.item;
        const id = item?.id || mediaEl.dataset.characterId;
        const g = item?.gender ?? mediaEl.dataset.gender;
        const fetched = await fetchPickerCardMedia(item || { id });
        if (fetched) {
          let target = mediaEl;
          if (fetched.kind === 'video' && !(mediaEl instanceof HTMLVideoElement)) {
            const video = createPickerCardMediaEl({ alt: item?.name, kind: 'video' });
            video.dataset.characterId = mediaEl.dataset.characterId;
            video.dataset.gender = mediaEl.dataset.gender;
            video.dataset.spriteLoaded = 'true';
            mediaEl.replaceWith(video);
            target = video;
          } else if (fetched.kind !== 'video' && mediaEl instanceof HTMLVideoElement) {
            const img = createPickerCardMediaEl({ alt: item?.name, kind: 'image' });
            img.dataset.characterId = mediaEl.dataset.characterId;
            img.dataset.gender = mediaEl.dataset.gender;
            img.dataset.spriteLoaded = 'true';
            img.onerror = () => { img.src = '/assets/images/kiss.webp'; };
            mediaEl.replaceWith(img);
            target = img;
          }
          bindPickerCardMedia(target, fetched);
          return;
        }
        const spriteUrl = await fetchSpriteUrlForCard(id, g);
        if (mediaEl instanceof HTMLVideoElement) {
          const img = createPickerCardMediaEl({ alt: item?.name, kind: 'image' });
          img.dataset.characterId = mediaEl.dataset.characterId;
          img.dataset.gender = mediaEl.dataset.gender;
          img.dataset.spriteLoaded = 'true';
          img.onerror = () => { img.src = '/assets/images/kiss.webp'; };
          img.src = spriteUrl;
          mediaEl.replaceWith(img);
          return;
        }
        mediaEl.src = spriteUrl;
      };

      const eagerCount = 12;
      mediaEls.slice(0, eagerCount).forEach((el) => { loadMedia(el); });

      observeManyWhenVisible(
        mediaEls.slice(eagerCount),
        (target) => loadMedia(target),
        {
          once: true,
          root: listWrap,
          rootMargin: '350px 0px',
          threshold: 0.01,
          fallback: (el) => setTimeout(() => loadMedia(el), 250),
        }
      );
    }
  };

  modal.onclick = (e) => {
    if (e.target === modal) handleClose();
  };

  cleanup.keydown = (event) => {
    const key = event.key || event.code;
    if (key !== 'Escape' && key !== 'Esc') return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (document.querySelector('.picker-character-info-tooltip')) {
      removePickerInfoTooltip();
      return;
    }
    handleClose();
  };
  document.addEventListener('keydown', cleanup.keydown, true);

  render();
  openModal(modal);
};

export const attachCreateVideoRankedCharacterPickerToTrigger = ({ selectId, getCharacters, getFilterIds, getTakenIds } = {}) => {
  const trigger = document.getElementById(`${selectId}-search-trigger`);
  if (!trigger) return;

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    if (trigger.dataset.pickerOpening === 'true') return;
    trigger.dataset.pickerOpening = 'true';
    void openCreateVideoRankedCharacterPicker({ selectId, getCharacters, getFilterIds, getTakenIds })
      .finally(() => { trigger.dataset.pickerOpening = 'false'; });
  };

  trigger.addEventListener('click', handleOpen, true);
  trigger.addEventListener('touchstart', handleOpen, { capture: true, passive: false });
  trigger.addEventListener('pointerdown', handleOpen, { capture: true });
  trigger.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') return handleOpen(e);
  }, true);
};

